import { useEffect, useId, useRef, useState, type ChangeEvent } from 'react'
import { Icon } from '@iconify/react'
import styles from '@/pages/CampanhasPage/CampanhaEditPage.module.css'
import uploadStyles from './CampaignImageUploadField.module.css'

export type CampaignImageUploadFieldProps = {
  label: string
  hint?: string
  emptyHint: string
  accept: string
  maxBytes: number
  validateFileSize: (file: File, maxBytes: number, label: string) => string | null
  file: File | null
  imageUrl: string | null
  onFileChange: (file: File | null) => void
  onFileError: (message: string | null) => void
  previewAlt: string
  inputId?: string
  validationLabel?: string
  required?: boolean
}

function useObjectUrl(file: File | null): string | null {
  const [preview, setPreview] = useState<{ file: File; url: string } | null>(null)

  useEffect(() => {
    if (!file) {
      return
    }

    const nextUrl = URL.createObjectURL(file)
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) {
        setPreview({ file, url: nextUrl })
      }
    })

    return () => {
      cancelled = true
      URL.revokeObjectURL(nextUrl)
    }
  }, [file])

  if (!file || preview?.file !== file) {
    return null
  }

  return preview.url
}

export function CampaignImageUploadField({
  label,
  hint,
  emptyHint,
  accept,
  maxBytes,
  validateFileSize,
  file,
  imageUrl,
  onFileChange,
  onFileError,
  previewAlt,
  inputId,
  validationLabel,
  required = false,
}: CampaignImageUploadFieldProps) {
  const generatedId = useId()
  const resolvedInputId = inputId ?? generatedId
  const inputRef = useRef<HTMLInputElement>(null)
  const filePreviewUrl = useObjectUrl(file)
  const previewUrl = file ? filePreviewUrl : imageUrl
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)

  const chooseFile = (nextFile: File) => {
    const allowed = accept.split(',').map((type) => type.trim().toLowerCase())
    const mime = nextFile.type.toLowerCase()
    const extension = `.${nextFile.name.split('.').pop()?.toLowerCase()}`
    const typeAllowed = allowed.some((type) =>
      type.startsWith('.')
        ? type === extension
        : type.endsWith('/*')
          ? mime.startsWith(type.slice(0, -1))
          : type === mime
    )
    const validationError = !typeAllowed
      ? 'Formato não aceito. Escolha uma imagem nos formatos indicados abaixo.'
      : validateFileSize(nextFile, maxBytes, validationLabel ?? label)
    setError(validationError)
    if (validationError) {
      onFileError(validationError)
      return
    }
    onFileError(null)
    onFileChange(nextFile)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0]
    event.target.value = ''
    if (nextFile) chooseFile(nextFile)
  }

  return (
    <div className={uploadStyles.field}>
      <label className={styles.editLabel} htmlFor={resolvedInputId}>
        <span>
          {label} {required && <span className={styles.requiredBadge}>Obrigatório</span>}
        </span>
        {hint ? <span className={styles.editLabelHint}>{hint}</span> : null}
      </label>
      <input
        ref={inputRef}
        id={resolvedInputId}
        type="file"
        accept={accept}
        className={styles.fileInput}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={`${resolvedInputId}-guidance`}
        onChange={handleFileChange}
      />
      <button
        type="button"
        className={`${uploadStyles.dropzone} ${dragging ? uploadStyles.dragging : ''}`}
        aria-label={`${previewUrl ? 'Trocar' : 'Escolher'} ${label.toLowerCase()}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          const droppedFile = event.dataTransfer.files[0]
          if (droppedFile) chooseFile(droppedFile)
        }}
      >
        {previewUrl ? (
          <div className={uploadStyles.preview}>
            <img key={previewUrl} src={previewUrl} alt={previewAlt} />
          </div>
        ) : (
          <>
            <Icon
              icon="mdi:image-plus-outline"
              style={{ fontSize: 28, color: 'var(--color-text-muted)' }}
              aria-hidden
            />
            <span className={uploadStyles.dropHint}>Arraste uma imagem até aqui</span>
            <span className={styles.editUploadButton}>Escolher imagem</span>
          </>
        )}
      </button>
      <p id={`${resolvedInputId}-guidance`} className={uploadStyles.guidance}>
        {emptyHint}
      </p>
      {error && (
        <p className={uploadStyles.error} role="alert">
          {error}
        </p>
      )}
      {(file || previewUrl) && (
        <div className={uploadStyles.actions}>
          <button type="button" onClick={() => inputRef.current?.click()}>
            <Icon icon="mdi:image-edit-outline" aria-hidden /> Trocar imagem
          </button>
          {previewUrl && (
            <button type="button" onClick={() => dialogRef.current?.showModal()}>
              <Icon icon="mdi:magnify-plus-outline" aria-hidden /> Ampliar
            </button>
          )}
          {file && (
            <button
              type="button"
              onClick={() => {
                onFileChange(null)
                setError(null)
                onFileError(null)
              }}
            >
              <Icon icon="mdi:undo" aria-hidden /> Desfazer troca
            </button>
          )}
        </div>
      )}
      {file && (
        <p className={uploadStyles.pending} role="status">
          <Icon icon="mdi:check-circle-outline" aria-hidden />{' '}
          <span>
            <strong>{file.name}</strong> · {(file.size / 1024 / 1024).toFixed(1)} MB
            <br />
            Imagem selecionada. Salve a campanha para aplicar.
          </span>
        </p>
      )}
      <dialog
        ref={dialogRef}
        className={uploadStyles.dialog}
        aria-label="Visualização ampliada da imagem"
      >
        <div className={uploadStyles.dialogHeader}>
          <strong>{label}</strong>
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            aria-label="Fechar imagem"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>
        {previewUrl && <img src={previewUrl} alt={`Imagem ampliada: ${label}`} />}
      </dialog>
    </div>
  )
}
